import { apiStatusCodes } from '../config/global-variables';
import loginModel, {clearCredentials} from './login.model';
import navigationModel from "./navigation/navigation.model";
import RefreshService from '../service/refresh.service';
import apiResponseTimeService from '../service/responseTime.service'
import { v4 as uuidv4 } from 'uuid';
import getRequestURL from '../utils/getRequestURL';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
    failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve());
    failedQueue = [];
};

/**
 * Retry a request with a raw m.request — bypasses 401/refresh logic so a
 * consistently-failing API cannot trigger another refresh cycle.
 */
const retryRaw = (path, params, successCallBack, failureCallBack) => {
    let reqparm = Object.assign({
        url: getRequestURL(path),
        headers: {},
        withCredentials: true
    }, params);

    if (navigationModel.getCurrentMenuID()) {
        reqparm.headers['From'] = navigationModel.getCurrentMenuID();
    }

    m.request(reqparm)
        .then(successCallBack)
        .catch(failureCallBack);
};

const modelBase = {
    isAuthorized: () => {
        if (!loginModel.isAuthenticated()) {
            throw { code: 401, message: "Login required." };
        }
    },

    downloadBlob: (data, fileName) => {
        try {
            if (typeof data === 'string' || data instanceof String) {
                var link = document.createElement("a");
                link.setAttribute('href', data);
                link.setAttribute('target', "_blank");
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                if (navigator.msSaveBlob) {
                    navigator.msSaveBlob(data, fileName);
                } else {
                    var link = document.createElement('a');
                    if (link.download !== undefined) {
                        var url = URL.createObjectURL(data);
                        link.setAttribute('href', url);
                        link.setAttribute('download', fileName);
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                }
            }
        }
        catch (e) {
            throw e;
        }
    },
    request: (successCallBack, failureCallBack, path, params) => {
        try {
            modelBase.isAuthorized();
            const newGuid = uuidv4();
            params.config = function (xhr) {
                apiResponseTimeService.addXhr(newGuid, xhr);
            };
            
            let reqparm = Object.assign({
                url: getRequestURL(path),
                headers: {},
                withCredentials: true
            },
                params);

            if (navigationModel.getCurrentMenuID()) {
                reqparm.headers['From'] = navigationModel.getCurrentMenuID();
            }

            return m.request(reqparm)
                .then((result) => {
                    successCallBack(result);
                }).catch((requestError) => {
                    // Not session timeout error
                    if (requestError.code !== apiStatusCodes.unauthorized) {
                        failureCallBack(requestError);
                        return;
                    }

                    // Skip retry if already attempting a token refresh to prevent infinite loop
                    if (isRefreshing) {

                        // Queue the request to be retried after refresh completes
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        }).then(() => retryRaw(path, params, successCallBack, failureCallBack))
                         .catch(failureCallBack);
                    }
                    isRefreshing = true;

                    return RefreshService.refresh()
                        .then(() => {
                            // After refresh make request again
                            processQueue(null);
                            isRefreshing = false;
                            retryRaw(path, params, successCallBack, failureCallBack);
                        })
                        .catch((refreshError) => {
                            processQueue(refreshError);
                            isRefreshing = false;
                            // Refresh failed — session is dead, redirect to login
                            clearCredentials();
                            sessionStorage.removeItem('mcs_last_route');
                            sessionStorage.removeItem('mcs_agreementYN');
                            m.route.set('/login');
                            failureCallBack(refreshError);
                        });
                }).finally(() => {
                    apiResponseTimeService.process(newGuid);
                });

        } catch (e) {
            failureCallBack(e);
        }
    },

    postDownload: (successCallBack, failureCallBack, path, params, fileName) => {
        if (params == undefined) {
            params = {};
        }

        params = Object.assign(params, { responseType: "blob" });
        modelBase.post((data) => {
            try {
                modelBase.downloadBlob(data, fileName);
                successCallBack();
            } catch (e) {
                failureCallBack(e);
            }
        }, failureCallBack, path, params);
    },

    getDownload: (successCallBack, failureCallBack, path, params, fileName) => {
        if (params == undefined) {
            params = {};
        }

        params = Object.assign(params, { responseType: "blob" });
        modelBase.get((data) => {
            try {
                modelBase.downloadBlob(data, fileName);
                successCallBack();
            } catch (e) {
                failureCallBack(e);
            }
        }, failureCallBack, path, params);
    },

    getImageUrl: (successCallBack, failureCallBack, fileId) => {

        var params = { responseType: "blob", params: { fileId: fileId } };

        modelBase.get((data) => {
            try {
                var urlCreator = window.URL || window.webkitURL;
                var imageUrl = urlCreator.createObjectURL(data);
                successCallBack(imageUrl);
            } catch (e) {
                failureCallBack(e);
            }
        }, failureCallBack, `/api/file/:fileId`, params);
    },

    post: (successCallBack, failureCallBack, path, params) => {

        if (params == undefined) {
            params = {};
        }
        params = Object.assign(params, { method: 'POST' });
        modelBase.request(successCallBack, failureCallBack, path, params);
    },

    get: (successCallBack, failureCallBack, path, params) => {
        if (params == undefined) {
            params = {};
        }
        params = Object.assign(params, { method: 'GET' });
        modelBase.request(successCallBack, failureCallBack, path, params);
    },

    put: (successCallBack, failureCallBack, path, params) => {
        if (params == undefined) {
            params = {};
        }
        params = Object.assign(params, { method: 'PUT' });
        modelBase.request(successCallBack, failureCallBack, path, params);
    },

    uploadfile: (successCallBack, failureCallBack, file, category) => {
        let formData = new FormData();
        formData.append("File0", file, file.name);
        let params = { method: 'POST', body: formData, params: { category: category } };
        modelBase.request(successCallBack, failureCallBack, `/api/file/:category`, params);
    },

    toQueryString: (obj, prefix) => {
        var str = [],
            p;
        for (p in obj) {
            if (obj.hasOwnProperty(p)) {
                var k = prefix ? prefix + "[" + p + "]" : p,
                    v = obj[p];
                str.push((v !== null && typeof v === "object") ?
                    serialize(v, k) :
                    encodeURIComponent(k) + "=" + encodeURIComponent(v));
            }
        }
        return str.join("&");
    }
}
export default modelBase;